import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { logActivity } from '@/lib/activityLog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientLogin({ onLogin }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  // Check for session expired flag
  useEffect(() => {
    const sessionExpired = localStorage.getItem('session_expired');
    if (sessionExpired) {
      toast.info('Your session has expired. Please log in again.');
      localStorage.removeItem('session_expired');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('[ClientLogin] Form submitted, preventDefault called');
    
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoading(true);

    try {
      console.log('[ClientLogin] Attempting login for:', email);
      
      // Clear any stale authorization header before login
      delete axios.defaults.headers.common['Authorization'];
      
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password
      });

      console.log('[ClientLogin] Response received:', response.data);
      const { access_token, user } = response.data;
      
      // Verify the logged-in user is a client
      if (user.role !== 'client') {
        toast.error('This login is for clients only. Coaches should use the coach login.');
        setLoading(false);
        return;
      }
      
      console.log('[ClientLogin] Calling onLogin with user:', user.email);
      onLogin(user, access_token);
      logActivity('login');
      toast.success('Client login successful!');
      
      // Redirect based on onboarding completion
      if (!user.onboarding_completed) {
        console.log('[ClientLogin] Onboarding not completed, redirecting to /app/onboarding');
        window.location.href = '/app/onboarding';
      } else {
        console.log('[ClientLogin] Onboarding completed, redirecting to /app');
        window.location.href = '/app';
      }
    } catch (error) {
      console.error('[ClientLogin] Login error:', error);
      console.error('[ClientLogin] Error response:', error.response?.data);
      
      // Handle different error response formats
      let errorMessage = 'Login failed';
      const errorData = error.response?.data;
      
      if (errorData) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors come as an array
          errorMessage = errorData.detail.map(e => e.msg || e.message || 'Validation error').join(', ');
        } else if (typeof errorData.detail === 'object' && errorData.detail?.msg) {
          errorMessage = errorData.detail.msg;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
      <div className="w-full max-w-md">
        <a 
          href="/" 
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to homepage
        </a>
        <Card className="w-full" data-testid="client-login-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <a href="/" className="cursor-pointer hover:opacity-80 transition-opacity">
                <img src="/logo.png" alt="MealGuide" className="h-24" />
              </a>
            </div>
            <CardDescription className="text-base">Client Portal Login</CardDescription>
          </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" action="#">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="client@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="client-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="client-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
              disabled={loading}
              data-testid="client-login-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
            
            <div className="text-center space-y-2">
              <button
                type="button"
                onClick={() => navigate('/app/forgot-password')}
                className="text-sm text-blue-600 hover:underline"
              >
                Forgot your password?
              </button>
              
              <p className="text-sm text-gray-600">
                Don't have an account?{' '}
                <button
                  onClick={() => window.location.href = '/signup'}
                  className="text-blue-600 font-medium hover:underline"
                >
                  Sign up
                </button>
              </p>
            </div>
          </form>
        </CardContent>
        </Card>
      </div>
    </div>
  );
}
